import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { deleteInvoiceProcessStopperRule } from './deleteInvoiceProcessStopperRule';

/**
 * A custom hook to delete the stopper rule for an invoice process with a given stopperRuleId.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 * @param stopperRuleId - The ID of the invoice stopper rule to delete.
 * @returns A promise that resolves when the stopper rule is deleted.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/delete_invoice_process_stopper_rule_vadmin_invoice_stopper_rule__stopper_rule_id__delete
 */
export const useMutationDeleteInvoiceProcessStopperRule = (
  options?: UseMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stopperRuleId: UUID) =>
      deleteInvoiceProcessStopperRule(stopperRuleId),
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getStopperRules({}),
      });
    },
    ...options,
  });
};
